import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * MobileSessionHeader - 2025 Compact Header for Active Calls
 *
 * Design Principles (from UX_REFACTORING_GUIDE.md):
 * - "Simplicity Wins" - Show only essential info during calls
 * - "Make friend presence visible everywhere"
 * - 36px height vs 60px full header = more screen for content
 *
 * Inspired by:
 * - FaceTime: Timer + participants in minimal bar
 * - Discord: Friend avatars with speaking indicators
 * - Google Meet: Compact info bar during calls
 */
const MobileSessionHeader = ({
  sessionDuration = 0,      // seconds
  friends = [],             // [{ name, avatar, isSpeaking }]
  onMenuOpen,               // Opens overflow menu
  onFriendClick,            // Click on friend avatar
  connectionQuality = 'good', // 'good' | 'poor' | 'bad'
  isRecording = false,      // Show recording indicator
}) => {
  // Format duration as MM:SS or HH:MM:SS
  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Connection quality color
  const getQualityColor = () => {
    switch (connectionQuality) {
      case 'poor': return '#f59e0b'; // amber
      case 'bad': return '#ef4444';  // red
      default: return '#10b981';     // green
    }
  };

  return (
    <motion.header
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -50, opacity: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        height: '36px',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        background: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 12px',
        paddingTop: 'max(env(safe-area-inset-top, 0px), 0px)',
      }}
    >
      {/* Left: Live indicator + Timer */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        {/* Live/Recording indicator */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 10px',
          background: isRecording
            ? 'rgba(239, 68, 68, 0.15)'
            : 'rgba(16, 185, 129, 0.15)',
          borderRadius: '100px',
        }}>
          {/* Pulsing dot */}
          <span style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: isRecording ? '#ef4444' : getQualityColor(),
            boxShadow: `0 0 8px ${isRecording ? 'rgba(239, 68, 68, 0.5)' : 'rgba(16, 185, 129, 0.5)'}`,
            animation: 'pulse 2s ease-in-out infinite',
          }} />

          {/* Timer */}
          <span style={{
            fontSize: '13px',
            fontWeight: '600',
            fontVariantNumeric: 'tabular-nums',
            color: isRecording ? '#ef4444' : '#10b981',
            letterSpacing: '-0.01em',
          }}>
            {formatDuration(sessionDuration)}
          </span>
        </div>

        {/* Connection quality indicator (only show if not good) */}
        {connectionQuality !== 'good' && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px',
            background: connectionQuality === 'poor'
              ? 'rgba(245, 158, 11, 0.15)'
              : 'rgba(239, 68, 68, 0.15)',
            borderRadius: '100px',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={getQualityColor()} strokeWidth="2.5">
              <path d="M5 12.55a11 11 0 0 1 14.08 0" />
              <path d="M1.42 9a16 16 0 0 1 21.16 0" />
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
              <circle cx="12" cy="20" r="1" fill={getQualityColor()} />
            </svg>
          </div>
        )}
      </div>

      {/* Center: Friend avatars */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        flex: 1,
        justifyContent: 'center',
        overflow: 'hidden',
      }}>
        <AnimatePresence>
          {friends.slice(0, 4).map((friend, index) => (
            <motion.button
              key={friend.id || friend.name || index}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => onFriendClick?.(friend)}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                border: friend.isSpeaking
                  ? '2px solid #10b981'
                  : '2px solid rgba(255, 255, 255, 0.2)',
                background: friend.avatar
                  ? `url(${friend.avatar}) center/cover`
                  : 'linear-gradient(135deg, #297A92 0%, #42909E 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '600',
                color: 'white',
                cursor: 'pointer',
                padding: 0,
                boxShadow: friend.isSpeaking
                  ? '0 0 0 3px rgba(16, 185, 129, 0.3)'
                  : 'none',
                transition: 'all 0.2s ease',
              }}
              title={friend.name}
            >
              {!friend.avatar && friend.name?.charAt(0).toUpperCase()}
            </motion.button>
          ))}

          {/* +N more indicator */}
          {friends.length > 4 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'rgba(255, 255, 255, 0.6)',
                marginLeft: '4px',
              }}
            >
              +{friends.length - 4}
            </motion.span>
          )}
        </AnimatePresence>

        {/* Empty state - no friends */}
        {friends.length === 0 && (
          <span style={{
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.5)',
          }}>
            Solo session
          </span>
        )}
      </div>

      {/* Right: Menu button */}
      <button
        onClick={onMenuOpen}
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255, 255, 255, 0.7)',
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
          e.currentTarget.style.color = 'white';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </svg>
      </button>

      {/* Pulse animation keyframes */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.1); }
        }
      `}</style>
    </motion.header>
  );
};

export default MobileSessionHeader;
